<template>
  <!-- RAIL MODE: Flyout menu (no drag support) - no wrapper div to match other menu items -->
  <v-menu
    v-if="rail && items.length"
    v-model="flyoutOpen"
    location="end"
    :close-on-content-click="false"
    :open-on-hover="!isTouchDevice"
    :open-on-click="isTouchDevice"
    :open-delay="FLYOUT_OPEN_DELAY"
    :close-delay="FLYOUT_CLOSE_DELAY"
  >
    <template #activator="{ props }">
      <v-list-item
        v-bind="props"
        :aria-label="$t('routes.favorites')"
        :class="{ 'rail-item-active': hasActiveChild }"
        data-cy="favorites"
      >
        <template #prepend>
          <v-icon size="default" class="custom-nav-color">
            mdi-star
          </v-icon>
        </template>
      </v-list-item>
    </template>

    <v-list
      density="compact"
      nav
      class="flyout-menu"
    >
      <v-list-subheader class="flyout-header">
        {{ $t('routes.favorites') }}
      </v-list-subheader>

      <v-list-item
        v-for="item in items"
        :key="`flyout-${item.link}`"
        :to="{ name: item.link, params: { lang: $current_lang } }"
        :active="isActiveRoute(item)"
        active-class="customDrawerSelect"
        :data-cy="item.text"
        @click="handleFlyoutItemClick($event)"
        @mouseenter="flyoutHoveringKey = item.link"
        @mouseleave="flyoutHoveringKey = null"
      >
        <template #prepend>
          <v-icon size="small" :class="menuLinkText">
            {{ item.icon || 'mdi-circle-medium' }}
          </v-icon>
        </template>
        <v-list-item-title :class="[menuLinkText, 'custom-color']">
          {{ item.text }}
        </v-list-item-title>
        <template
          v-if="flyoutHoveringKey === item.link || isTouchDevice"
          #append
        >
          <FavoriteStar
            :is-favorite="true"
            force-visible
            size="x-small"
            @toggle="removeFavorite(item.link)"
          />
        </template>
      </v-list-item>
    </v-list>
  </v-menu>

  <!-- EXPANDED MODE: Custom expandable with draggable - uses wrapper div -->
  <div
    v-else-if="items.length"
    class="favorites-section"
  >
    <v-list-item
      class="favorites-header"
      @click="expanded = !expanded"
    >
      <template #prepend>
        <v-icon size="default" class="custom-nav-color">
          mdi-star
        </v-icon>
      </template>
      <v-list-item-title class="latest-nav-custom-color">
        {{ $t('routes.favorites') }}
      </v-list-item-title>
      <template #append>
        <v-icon size="small">
          {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
        </v-icon>
      </template>
    </v-list-item>

    <v-expand-transition>
      <v-list
        v-show="expanded"
        class="favorites-list"
        density="compact"
        nav
        bg-color="transparent"
      >
        <draggable
          v-model="orderedItems"
          item-key="link"
          handle=".drag-handle"
          :animation="200"
          ghost-class="favorite-ghost"
          chosen-class="favorite-chosen"
          drag-class="favorite-drag"
          @start="isDragging = true"
          @end="onDragEnd"
        >
          <template #item="{ element }">
            <v-list-item
              :key="`fav-${element.link}-${navigationSource}`"
              :to="{ name: element.link, params: { lang: $current_lang } }"
              :active="isActiveRoute(element)"
              active-class="customDrawerSelect"
              class="favorite-item"
              :data-cy="element.text"
              @click="handleFavoriteClick($event)"
              @mouseenter="hoveringKey = element.link"
              @mouseleave="hoveringKey = null"
            >
              <template #prepend>
                <v-icon
                  class="drag-handle"
                  size="x-small"
                  :aria-label="$t('hints.dragToReorder')"
                  :class="{ 'drag-handle--visible': hoveringKey === element.link || isTouchDevice }"
                >
                  mdi-drag-vertical
                </v-icon>
                <v-icon size="small" :class="menuLinkText">
                  {{ element.icon || 'mdi-circle-medium' }}
                </v-icon>
              </template>
              <v-list-item-title :class="[menuLinkText, 'custom-color']">
                {{ element.text }}
              </v-list-item-title>
              <template
                v-if="(hoveringKey === element.link || isTouchDevice) && !isDragging"
                #append
              >
                <FavoriteStar
                  :is-favorite="true"
                  force-visible
                  size="x-small"
                  @toggle="removeFavorite(element.link)"
                />
              </template>
            </v-list-item>
          </template>
        </draggable>
      </v-list>
    </v-expand-transition>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import FavoriteStar from './FavoriteStar.vue'
import { bus } from '@/main'
import { mapState } from 'vuex'
import { useTheme } from 'vuetify'
import {
  FLYOUT_OPEN_DELAY,
  FLYOUT_CLOSE_DELAY,
  FLYOUT_NAVIGATION_CLOSE_DELAY
} from '@/utils/constants'

export default {
  name: 'FavoritesSection',
  components: { draggable, FavoriteStar },
  props: {
    items: { type: Array, required: true },
    rail: { type: Boolean, default: false },
    openFlyoutKey: { type: [String, null], default: null }
  },
  emits: ['update:flyout'],
  data() {
    return {
      expanded: false,
      isDragging: false,
      isTouchDevice: false,
      hoveringKey: null,
      flyoutHoveringKey: null,
      closeTimeout: null,
      // Expose constants to template
      FLYOUT_OPEN_DELAY,
      FLYOUT_CLOSE_DELAY
    }
  },
  computed: {
    ...mapState('app', ['navigationSource']),
    // Flyout state - managed by parent via openFlyoutKey prop (matching FlowyNavMenuItem pattern)
    flyoutOpen: {
      get() {
        return this.openFlyoutKey === 'favorites'
      },
      set(isOpen) {
        // Clear any pending timeout to prevent race conditions
        if (this.closeTimeout) {
          clearTimeout(this.closeTimeout)
          this.closeTimeout = null
        }
        this.$emit('update:flyout', 'favorites', isOpen)
      }
    },
    orderedItems: {
      get() {
        return [...this.items]
      },
      set(newOrder) {
        // Map back to string array of route links
        const orderedLinks = newOrder.map(item => item.link)
        this.$store.commit('app/setFavorites', orderedLinks)
      }
    },
    menuLinkText() {
      return { 'text-white': this.isThemeDark }
    },
    isThemeDark() {
      const theme = useTheme()
      return theme.global.name.value === 'dark'
    },
    hasActiveChild() {
      return this.items.some(item => this.isActiveRoute(item))
    }
  },
  watch: {
    '$route'() {
      // Close flyout on navigation (matching FlowyNavMenuItem pattern)
      if (this.flyoutOpen) {
        if (this.closeTimeout) {
          clearTimeout(this.closeTimeout)
        }
        // Brief delay for visual feedback before closing
        this.closeTimeout = setTimeout(() => {
          this.$emit('update:flyout', 'favorites', false)
          this.closeTimeout = null
        }, FLYOUT_NAVIGATION_CLOSE_DELAY)
      }
    },
    // Force re-render when navigation source changes to ensure proper highlight state
    navigationSource() {
      this.$forceUpdate()
    }
  },
  mounted() {
    try {
      this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    } catch {
      this.isTouchDevice = false
    }
    // Listen for menu navigation events to ensure we update highlight state
    bus.$on('menuNavigation', this.onMenuNavigation)
  },
  beforeUnmount() {
    // Cleanup event listener
    bus.$off('menuNavigation', this.onMenuNavigation)
    // Cleanup pending timeout to prevent memory leaks
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
  },
  methods: {
    // Force update when menu navigation happens
    onMenuNavigation() {
      this.$forceUpdate()
    },
    onDragEnd() {
      this.isDragging = false
      // Trigger profile save via event bus
      bus.$emit('saveUserProfile')
    },
    // Route-based active state with symmetric logic to FlowyNavMenuItem.shouldSuppressFavorite():
    // - Favorites highlights when: navigationSource === 'favorites'
    // - Menu suppresses when: navigationSource === 'favorites'
    // Result: Favorites wins only for explicit favorites clicks,
    //         Menu wins for menu clicks AND direct URL navigation (default)
    isActiveRoute(item) {
      const routeMatches = this.$route.name === item.link
      return routeMatches && this.navigationSource === 'favorites'
    },
    // Expanded mode - set navigation source before :to navigates
    handleFavoriteClick(event) {
      // Skip side effects for modifier-key clicks (browser opens new tab via :to href)
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return
      // Prevent click during drag
      if (this.isDragging) {
        event.preventDefault()
        event.stopPropagation()
        return
      }

      // Keep expanded section open
      this.expanded = true

      // Set navigation source BEFORE :to navigation completes
      this.$store.commit('app/setNavigationSource', 'favorites')
      bus.$emit('favoriteNavigation')
      // Navigation is handled by the :to prop - no router.push needed
    },
    // Rail mode flyout items - set navigation source before :to navigates
    handleFlyoutItemClick(event) {
      // Skip side effects for modifier-key clicks (browser opens new tab via :to href)
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return
      this.$store.commit('app/setNavigationSource', 'favorites')
      bus.$emit('favoriteNavigation')
      // Navigation is handled by the :to prop - no router.push needed
    },
    // Remove item from favorites - action expects menuItem object with link property
    removeFavorite(routeLink) {
      this.$store.dispatch('app/toggleFavorite', { link: routeLink })
    }
  }
}
</script>

<style lang="scss">
.favorites-section {
  .favorites-header {
    cursor: pointer;
  }

  .favorites-list {
    padding-left: 0;
  }

  .favorite-item {
    padding-left: 16px;
    cursor: pointer;

    // Disable text selection during drag
    user-select: none;
  }

  .drag-handle {
    cursor: grab;
    opacity: 0;
    transition: opacity 0.2s ease;
    margin-right: 4px;

    &--visible {
      opacity: 0.6;
    }

    &:hover {
      opacity: 1 !important;
    }

    &:active {
      cursor: grabbing;
    }
  }

  .favorite-ghost {
    opacity: 0.4;
    background: rgba(var(--v-theme-primary), 0.1);
    border-radius: 4px;
  }

  .favorite-chosen {
    background: rgba(var(--v-theme-primary), 0.15);
  }

  .favorite-drag {
    opacity: 1;
    background: rgba(var(--v-theme-surface), 0.95);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border-radius: 4px;
  }
}
</style>
